/**
 * CartPage — Cart, checkout, and order confirmation — WP-SL-V5 (DOPLA-688)
 *
 * Vertical-level cart (B2B and B2C identical flow shape — Stephan 2026-06-11).
 * Payment integration STUBBED until ADR-025 — no fake PSP responses.
 *
 * Props:
 *   items   — CartItem[] passed from App/MarketplacePage via cart state
 *   onClear — callback to empty the cart after successful checkout
 *   onNavigate — router navigation
 *
 * Ref: SL-R-031, WP-SL-V5, ADR-025 (pending)
 */

import { useState } from 'react';
import { useAuth } from '../auth/KeycloakProvider';
import { useConfig } from '../config/ConfigContext';
import type { Route } from '../shell/Router';

export interface CartItem {
  service_id: string;
  service_name: string;
  plan_id: string;
  plan_name: string;
  price_cents: number;
  currency: string;
  trial_days: number;
}

interface CartPageProps {
  items: CartItem[];
  onClear: () => void;
  onNavigate: (path: Route) => void;
  onRemove: (service_id: string, plan_id: string) => void;
}

type Rail = 'b2b_invoice' | 'b2c_card';
type CheckoutState = 'idle' | 'submitting' | 'done' | 'error';

function fmt(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-CH', { style: 'currency', currency }).format(cents / 100);
}

export default function CartPage({ items, onClear, onNavigate, onRemove }: CartPageProps) {
  const { token } = useAuth();
  const config = useConfig();
  // FADP/consumer posture (ADR-046): shown in B2C checkout per dispatch 2026-06-12
  const isB2C = config.instance.broker_type === 'b2c';
  const [rail, setRail] = useState<Rail>('b2b_invoice');
  const [checkoutState, setCheckoutState] = useState<CheckoutState>('idle');
  const [orderId, setOrderId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const total = items.reduce((s, i) => s + i.price_cents, 0);
  const currency = items[0]?.currency ?? 'CHF';

  async function handleCheckout() {
    if (!token || items.length === 0) return;
    setCheckoutState('submitting');
    setErrorMsg(null);
    try {
      const res = await fetch('/api/broker/v1/orders', {
        cache: 'no-store',
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map((i) => ({ service_id: i.service_id, plan_id: i.plan_id })),
          rail,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        setErrorMsg(err.error ?? `HTTP ${res.status}`);
        setCheckoutState('error');
        return;
      }
      const data = await res.json() as { order_id: string };
      setOrderId(data.order_id);
      setCheckoutState('done');
      onClear();
    } catch (e) {
      setErrorMsg('Network error');
      setCheckoutState('error');
    }
  }

  // ── Confirmation screen ────────────────────────────────────────────────────
  if (checkoutState === 'done' && orderId) {
    return (
      <div className="dp-shell__content" style={{ maxWidth: 560 }}>
        <div style={{
          padding: 'var(--sp-8)', background: 'var(--dp-forest-100)',
          borderRadius: 'var(--radius-lg)', textAlign: 'center', marginBottom: 'var(--sp-6)',
        }}>
          <p className="dp-eyebrow" style={{ color: 'var(--dp-forest-700)' }}>Order placed</p>
          <h2 style={{ margin: 'var(--sp-2) 0 var(--sp-1)' }}>Checkout complete</h2>
          <p style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)',
            color: 'var(--fg-3)', marginBottom: 'var(--sp-3)',
          }}>
            Order ID: {orderId}
          </p>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-2)' }}>
            {rail === 'b2b_invoice'
              ? 'An invoice will be raised. Settlement is recorded manually by the operator.'
              : 'Card payment integration is pending (ADR-025). Settlement will be recorded manually.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
          <button
            className="dp-btn dp-btn--primary"
            onClick={() => onNavigate('/admin/orders')}
          >
            View order history
          </button>
          <button
            className="dp-btn dp-btn--ghost"
            onClick={() => onNavigate('/')}
          >
            Back to catalogue
          </button>
        </div>
      </div>
    );
  }

  // ── Empty cart ─────────────────────────────────────────────────────────────
  if (items.length === 0) {
    return (
      <div className="dp-shell__content" style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 'var(--sp-4)', padding: 'var(--sp-12)',
        color: 'var(--fg-3)', fontFamily: 'var(--font-sans)',
      }}>
        <p className="dp-eyebrow">Cart</p>
        <h2>Your cart is empty</h2>
        <button className="dp-btn dp-btn--ghost" onClick={() => onNavigate('/')}>
          Browse catalogue
        </button>
      </div>
    );
  }

  // ── Cart view ──────────────────────────────────────────────────────────────
  return (
    <div className="dp-shell__content" style={{ maxWidth: 640 }}>
      <div style={{ marginBottom: 'var(--sp-6)' }}>
        <p className="dp-eyebrow">Cart</p>
        <h2 style={{ margin: 'var(--sp-1) 0 var(--sp-2)' }}>Your order</h2>
        <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
          Review your items and choose a payment path. Payment integration is pending (ADR-025) —
          settlement is recorded manually.
        </p>
      </div>

      {/* Items */}
      <div style={{
        border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-surface)', marginBottom: 'var(--sp-6)', overflow: 'hidden',
      }}>
        {items.map((item, idx) => (
          <div
            key={`${item.service_id}:${item.plan_id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--sp-4)',
              padding: 'var(--sp-4) var(--sp-5)',
              borderBottom: idx < items.length - 1 ? '1px solid var(--border-subtle)' : 'none',
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
                {item.service_name}
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', color: 'var(--fg-3)' }}>
                {item.plan_name}
              </div>
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-body-sm)', minWidth: 80, textAlign: 'right' }}>
              {item.price_cents === 0 && item.trial_days > 0
                ? <span style={{
                    background: 'var(--dp-teal-100)', color: 'var(--dp-teal-700)',
                    borderRadius: 'var(--radius-sm)', padding: '2px 7px', fontWeight: 600,
                  }}>TRIAL · {item.trial_days}d</span>
                : fmt(item.price_cents, item.currency)
              }
            </div>
            <button
              onClick={() => onRemove(item.service_id, item.plan_id)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--fg-4)', fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-meta)', padding: 'var(--sp-1)',
              }}
              title="Remove from cart"
            >
              ✕
            </button>
          </div>
        ))}

        {/* Total */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          padding: 'var(--sp-4) var(--sp-5)',
          borderTop: '2px solid var(--border-default)',
          background: 'var(--bg-recessed)',
        }}>
          <span style={{ fontWeight: 600, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
            Total
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
            {fmt(total, currency)}
          </span>
        </div>
      </div>

      {/* Rail selection */}
      <div style={{ marginBottom: 'var(--sp-6)' }}>
        <p style={{
          fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
          fontWeight: 600, marginBottom: 'var(--sp-3)', color: 'var(--fg-1)',
        }}>
          Payment path
        </p>
        <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
          {([
            { value: 'b2b_invoice', label: 'Invoice / PO', desc: 'Receive an invoice; settle by bank transfer or PO' },
            { value: 'b2c_card',    label: 'Card',          desc: 'Card payment (integration pending — manual settlement)' },
          ] as { value: Rail; label: string; desc: string }[]).map((opt) => (
            <label
              key={opt.value}
              style={{
                flex: 1, cursor: 'pointer', padding: 'var(--sp-4)',
                border: `2px solid ${rail === opt.value ? 'var(--dp-forest-600)' : 'var(--border-default)'}`,
                borderRadius: 'var(--radius-lg)',
                background: rail === opt.value ? 'var(--dp-forest-100)' : 'var(--bg-surface)',
                transition: 'border-color 0.15s',
              }}
            >
              <input
                type="radio"
                name="rail"
                value={opt.value}
                checked={rail === opt.value}
                onChange={() => setRail(opt.value)}
                style={{ marginRight: 'var(--sp-2)' }}
              />
              <span style={{ fontWeight: 600, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
                {opt.label}
              </span>
              <p style={{
                fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-meta)',
                color: 'var(--fg-3)', margin: 'var(--sp-1) 0 0 var(--sp-5)',
              }}>
                {opt.desc}
              </p>
            </label>
          ))}
        </div>
      </div>

      {/* FADP consumer-protection notice — B2C checkout (ADR-446, WP-SL-T4) */}
      {isB2C && (
        <div style={{
          background: 'var(--dp-teal-100)', borderRadius: 'var(--radius-md)',
          padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-4)',
          fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-meta)', color: 'var(--fg-2)',
          border: '1px solid var(--dp-teal-300)',
        }}>
          <strong>Data &amp; privacy notice</strong> — By placing this order you acknowledge
          that your personal data is processed by the service provider subject to Swiss FADP.
          Data residency: Switzerland. You may request deletion at any time.
          Card-on-file integration is pending; no payment is charged now.
        </div>
      )}

      {errorMsg && (
        <p style={{
          color: 'var(--dp-crimson-600)', fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body-sm)', marginBottom: 'var(--sp-4)',
        }}>
          Checkout failed: {errorMsg}
        </p>
      )}

      <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
        <button
          className="dp-btn dp-btn--primary"
          onClick={handleCheckout}
          disabled={checkoutState === 'submitting'}
        >
          {checkoutState === 'submitting' ? 'Placing order…' : 'Place order'}
        </button>
        <button
          className="dp-btn dp-btn--ghost"
          onClick={() => onNavigate('/')}
          disabled={checkoutState === 'submitting'}
        >
          Continue browsing
        </button>
      </div>
    </div>
  );
}
